<script setup lang="ts">
/**
 * ACRA Risk Assessment Form (CDD) — Aura 새 버전(RFA Guidelines Issued on 9 May 2025) 원문 그대로.
 *   PART 2 – RISK ASSESSMENT FORM (FOR CSP'S INTERNAL USE)
 * Yes / No / NA 는 텍스트 입력이 아니라 ☐/☒ 체크 토글.
 * 키(문서 변수와 1:1): srv_a_1~6(+_details) · b1_q1~19 · b2_q1~4 · b3_q1~11 · customer_risk_rating ·
 *   risk_rating_reasons · relies_on_third_party · recommendation · approval · recommending/approving_officer_*.
 *   B1 은 4a/4b 가 두 행이라 행 순서대로 b1_q1~19 (예전 양식 1~17번과 순서·의미 동일).
 */
import { onBeforeUnmount, onMounted, reactive, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import SgPreSigners from './SgPreSigners.vue';
import { SG_PRE_SIGNER_KEYS, useSgPreSigners } from './cdd/sgPreSignersContext';
import './allotDoc.css';

const fill = useCddFill();
const signersCtx = useSgPreSigners();

interface Item { no: string; text: string; sub?: string[]; note?: string }

const SERVICES: Item[] = [
  { no: '1)', text: 'Forming, on behalf of another person, a corporation or other legal person' },
  {
    no: '2)', text: 'Acting, or arranging for another person to act –',
    sub: ['i) as a director or secretary of a corporation;', 'ii) as a partner of a partnership; or', 'iii) in a similar capacity in relation to other legal persons'],
  },
  { no: '3)', text: 'Providing a registered office, business address, correspondence or administrative address or other related services for a corporation, partnership or other legal person' },
  { no: '4)', text: 'Acting, or arranging for another person to act, as a nominee shareholder on behalf of any corporation, other than a corporation whose securities are listed on an approved exchange within the meaning of section 2(1) of the Securities and Futures Act 2001' },
  { no: '5)', text: 'Carrying out any designated activity in relation to the provision of any accounting service' },
  {
    no: '6)', text: 'Carrying out an ACRA transaction with the ACRA Registrar using the electronic transaction system –',
    sub: ['i) on behalf of another person; or', 'ii) for one or more companies as a secretary of each of those companies'],
  },
];

const B1: Item[] = [
  { no: '1)', text: 'Is this a new customer?' },
  { no: '2)', text: 'Is the customer a public company listed on any stock exchange and exempted from disclosure requirements?' },
  { no: '3)', text: 'Is the customer a legal person or an entity that can hold assets in its own name?' },
  { no: '4a)', text: 'Does the customer use nominee director(s) or shareholder(s)?' },
  { no: '4b)', text: 'Where applicable, do the nominee shareholders represent majority ownership?' },
  { no: '5)', text: 'Does the ownership structure of the customer appear unusual or excessively complex given the nature of its business?' },
  { no: '6)', text: 'Is the customer’s business cash-intensive?' },
  { no: '7)', text: 'Does the customer frequently make unaccounted cash transactions to similar recipient(s)?' },
  { no: '8)', text: 'Do the proposed directors/partners/shareholders have prior criminal convictions involving fraud or dishonesty?' },
  { no: '9)', text: 'Is any of the customer, beneficial owner or its agent a politically-exposed person?' },
  { no: '10)', text: 'Are the customer’s company financial statements outdated?' },
  { no: '11)', text: 'Do the customer’s shareholders and/or directors frequently change, and the changes are within reason?' },
  { no: '12)', text: 'Is there any problem obtaining the required information in the relevant form?' },
  { no: '13)', text: 'Can the information obtained be verified by independent and reliable sources?' },
  { no: '14)', text: 'Is the customer a charitable or non-profit organisation which is not registered in Singapore?', note: '26' },
  { no: '15)', text: 'Does the customer appear to be a shell company?' },
  { no: '16)', text: 'Is the customer in a high-risk industry?', note: '27' },
  {
    no: '17)',
    text: 'The registered CSP has performed screening of particulars of the customer, beneficial owners of the customer, persons acting on behalf of the customer, and connected parties of the customer against the required information sources, i.e., Alert list provided by ACRA, on Google, the sanctions lists published by the Office of Foreign Assets Control of the US Department of the Treasury, against the regulations under the United Nations Act 2001; First Schedule of the Terrorism (Suppression of Financing) Act 2002; and/or other third party screening database.',
    sub: ['Are there adverse news or information arising?'],
  },
  { no: '18)', text: 'Exceptions noted from particulars of the customer, beneficial owners of the customer, persons acting on behalf of the customer, or connected parties of the customer cannot be disposed of satisfactorily.' },
];

const B2: Item[] = [
  { no: '1)', text: 'Is the customer connected to or transacting with a country or territory in relation to which the FATF has called for countermeasures?' },
  { no: '2)', text: 'Is the customer connected to or transacting with a country or territory in relation to which the FATF has subject to increased monitoring?', note: '29' },
  { no: '3)', text: 'Is the customer connected to or transacting with a country or a territory that is sanctioned by a regulatory body, such as the United Nations (UN)?', note: '30' },
  { no: '4)', text: 'Is the customer connected to or transacting with a country or a territory that is identified to be funding or supporting terrorist activities or that has designated terrorist organisations operating within their territories?' },
];

const B3: Item[] = [
  { no: '1)', text: 'Has the customer given any instruction to perform a transaction (which may include cash) anonymously?' },
  { no: '2)', text: 'Has the customer transferred any funds without providing underlying services or transactions?' },
  { no: '3)', text: 'Are there unusual patterns of transactions that have no apparent economic purpose or large cash payments, in which disbursement would have been normally made by other modes of payment (such as cheque, bank drafts etc.)?' },
  { no: '4)', text: 'Are there unaccounted payments received from unknown or un-associated third parties for services and/or transactions provided by the customer?' },
  { no: '5)', text: 'Is there instruction from the customer to incorporate “shell” companies with nominee shareholder(s) and/or director(s)?' },
  {
    no: '6)', text: 'Does the customer intend to purchase companies or business entities with no apparent commercial purpose?',
    sub: [
      'This would include:',
      'a) Multi-layer, multi-country and complex group structures.',
      'b) Setting up entities in Singapore where there is no obvious commercial purpose, or any other personal or economic connection to the customer.',
    ],
  },
  { no: '7)', text: 'Is this business relationship being established without any physical meeting?' },
  { no: '8)', text: 'Are there any transactions being performed without any physical meeting?' },
  { no: '9)', text: 'Are the transactions required by the customer inconsistent with the professional intermediaries’ knowledge of the customer’s risk profile and nature of business?' },
  { no: '10)', text: 'Is the customer physically present for identification purposes in a proposed incorporation transaction?' },
  { no: '11)', text: 'Is the customer physically present for identification purposes in a proposed transaction involving the transfer of management or ownership or sale of a shelf company?' },
];

const FOOTNOTES: Array<[string, string]> = [
  ['26', 'Please refer to www.charities.gov.sg for more information.'],
  ['27', 'As determined by the firm, such as with reference to publications and guidance issued from time to time by the FATF.'],
  ['28', 'Refer to the US OFAC website available from https://sanctionssearch.ofac.treas.gov/.'],
  ['29', 'Refer to the FATF website for a list of jurisdictions which the FATF has subject to increased monitoring.'],
  ['30', 'Please refer to the MAS and MHA-IMC_TD website links to check on the lists of countries sanctioned by the UN.'],
  ['31', 'Please note that separation of duties is a good practice regarding having separate persons conduct risk assessments of customers and approve their acceptance.'],
  ['32', 'You should consider escalating to the compliance officer or senior management and/or filing a Suspicious Transaction Report where necessary.'],
];

type Yn = '' | 'yes' | 'no';
type YnNa = '' | 'yes' | 'no' | 'na';

const srvOn = reactive<Record<string, boolean>>({});
const b1Ans = reactive<Record<string, Yn>>({});
const b2Ans = reactive<Record<string, Yn>>({});
const b3Ans = reactive<Record<string, YnNa>>({});
const riskRating = reactive<{ value: '' | 'low' | 'medium' | 'high' }>({ value: '' });
const recommendation = reactive<{ value: '' | 'recommended' | 'not_recommended' }>({ value: '' });
const approval = reactive<{ value: '' | 'approved' | 'not_approved' }>({ value: '' });
const reliance = reactive<{ on: boolean }>({ on: false });

const trackedKeys = [
  ...SERVICES.map((_, i) => `srv_a_${i + 1}`),
  ...B1.map((_, i) => `b1_q${i + 1}`),
  ...B2.map((_, i) => `b2_q${i + 1}`),
  ...B3.map((_, i) => `b3_q${i + 1}`),
  'customer_risk_rating',
  'recommendation',
  'approval',
];

onMounted(() => {
  trackedKeys.forEach((k) => fill.registerKey(k));
  // Word 용 signature 텍스트 (Signers UI 와 동기화) — 진행률에서는 선택 취급
  fill.registerKey('recommending_officer_signature');
  fill.registerKey('approving_officer_signature');
  // 미체크 서비스는 '' 가 아니라 진행률에서 제외(isProgressFilled) — 값은 no 로 정규화
  for (let i = 1; i <= SERVICES.length; i++) {
    const k = `srv_a_${i}`;
    if (!String(fill.values[k] || '').trim()) fill.setValue(k, 'no');
  }
  syncFromFill();
});
onBeforeUnmount(() => {
  trackedKeys.forEach((k) => fill.unregisterKey(k));
  fill.unregisterKey('recommending_officer_signature');
  fill.unregisterKey('approving_officer_signature');
});

const yesNo = (v: string): Yn => (v === 'yes' || v === 'true' ? 'yes' : v === 'no' || v === 'false' ? 'no' : '');

/** Autofill / 외부 setValue → 로컬 체크 UI 동기화 */
function syncFromFill() {
  for (let i = 1; i <= SERVICES.length; i++) {
    const k = `srv_a_${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    srvOn[k] = v === 'yes' || v === 'true' || v === '1';
  }
  for (let i = 1; i <= B1.length; i++) {
    const k = `b1_q${i}`;
    b1Ans[k] = yesNo(String(fill.values[k] || '').toLowerCase());
  }
  for (let i = 1; i <= B2.length; i++) {
    const k = `b2_q${i}`;
    b2Ans[k] = yesNo(String(fill.values[k] || '').toLowerCase());
  }
  for (let i = 1; i <= B3.length; i++) {
    const k = `b3_q${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    b3Ans[k] = v === 'na' ? 'na' : yesNo(v);
  }
  const rr = String(fill.values.customer_risk_rating || fill.values.risk_rating || '').toLowerCase();
  riskRating.value = rr === 'low' || rr === 'medium' || rr === 'high' ? (rr as any) : '';
  const rec = String(fill.values.recommendation || '').toLowerCase();
  recommendation.value =
    rec === 'recommended' || rec === 'not_recommended' ? (rec as any) : '';
  const ap = String(fill.values.approval || '').toLowerCase();
  approval.value = ap === 'approved' || ap === 'not_approved' ? (ap as any) : '';
  reliance.on = String(fill.values.relies_on_third_party || '').toLowerCase() === 'yes';
  seedRiskSignersFromOfficerNames();
  syncSignatureTextFromSigners();
}

watch(
  () => [...trackedKeys.map((k) => fill.values[k]), fill.values.relies_on_third_party].join('|'),
  () => syncFromFill(),
);

watch(
  () =>
    [
      fill.values.recommending_officer_name,
      fill.values.recommending_officer_position,
      fill.values.approving_officer_name,
      fill.values.approving_officer_position,
    ].join('|'),
  () => {
    seedRiskSignersFromOfficerNames();
    syncSignatureTextFromSigners();
  },
);

watch(
  () =>
    [
      ...(signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskRecommend) || []).map((s) => s.name),
      ...(signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskApprove) || []).map((s) => s.name),
    ].join('|'),
  () => syncSignatureTextFromSigners(),
);

function toggleSrv(n: number) {
  const key = `srv_a_${n}`;
  srvOn[key] = !srvOn[key];
  fill.setValue(key, srvOn[key] ? 'yes' : 'no');
  if (!srvOn[key]) fill.setValue(`${key}_details`, '');
}

function setYn(map: Record<string, Yn>, key: string, val: Yn) {
  map[key] = map[key] === val ? '' : val;
  fill.setValue(key, map[key] === 'yes' ? 'yes' : map[key] === 'no' ? 'no' : '');
}

function setYnNa(map: Record<string, YnNa>, key: string, val: YnNa) {
  map[key] = map[key] === val ? '' : val;
  fill.setValue(key, map[key] || '');
}

function setOne(target: { value: string }, key: string, val: string) {
  target.value = target.value === val ? '' : val;
  fill.setValue(key, target.value);
}

/** Regulation 24(1) 제3자 의존 문단 — 원문 "where applicable" 이라 켤 때만 문서에 나온다 */
function toggleReliance() {
  reliance.on = !reliance.on;
  fill.setValue('relies_on_third_party', reliance.on ? 'yes' : 'no');
}

/** Officer Name/Position → Signers 위젯 (자동으로 넣은 서명자는 입력을 따라감, 직접 고른 서명자는 유지).
 *  전체 값을 넘긴다 — 담당자 4개 키만 넘기면 seed 가 다른 문서의 이사 자동 서명자를 빈 목록으로 덮어쓴다. */
function seedRiskSignersFromOfficerNames() {
  signersCtx.seedFromValuesIfEmpty({ ...fill.values });
}

/** Word `*_officer_signature` 텍스트 — Signers 첫 이름과 동기화 */
function syncSignatureTextFromSigners() {
  const rec = signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskRecommend);
  const app = signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskApprove);
  const recName = String(rec[0]?.name || '').trim();
  const appName = String(app[0]?.name || '').trim();
  if (recName) fill.setValue('recommending_officer_signature', recName);
  if (appName) fill.setValue('approving_officer_signature', appName);
}
</script>

<template>
  <div class="allot-doc risk-doc">
    <!-- 머리글 — 문서 원문(로고 + 회사 정보) 고정 표기 -->
    <div class="risk-letterhead">
      <div class="lh-name">AURA PARTNERS (SINGAPORE) PTE. LTD.</div>
      <div class="lh-meta">1 Phillip Street, #08-00, Royal One Phillip, Singapore 048692</div>
      <div class="lh-meta">Tel: +65 6221 1768 | Email: enquiry@aurapartners.com.sg</div>
      <div class="lh-meta">UEN: 201717043E</div>
      <div class="lh-meta">Website: https://aurapartners.com.sg</div>
    </div>

    <table class="cdd-tbl risk-tbl head-tbl">
      <tbody>
        <tr><td class="cdd-lcell" style="width:28%">Name of Company :</td><td><CddVarField field-key="entity_name" label="Name of Company" /></td></tr>
        <tr><td class="cdd-lcell">UEN No. :</td><td><CddVarField field-key="entity_uen" label="UEN No." /></td></tr>
      </tbody>
    </table>

    <p class="doc-sec-title"><u>PART 2 – RISK ASSESSMENT FORM</u> <span class="red">(FOR CSP’S INTERNAL USE)</span></p>
    <p class="doc-p">
      You should assess the ML/PF/TF risks that you may reasonably expect to face in the course of business for each
      customer. To do so, you must have regard to various factors such as the nature of services required by your customer,
      the customer’s inherent risk and risk arising from your customer’s business. The list and its sections below are not
      exhaustive and should be adapted to suit your business context.
    </p>

    <p class="doc-sec-title">Section A – Nature of Services Required by Customer</p>
    <table class="cdd-tbl risk-tbl">
      <!-- 원문 열 비율(서비스 · 선택 · 상세) — 선택 열 머리글이 두 줄이라 좁은 yn 폭을 쓰면 Details 와 겹친다 -->
      <colgroup>
        <col style="width:44%" />
        <col style="width:18%" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th>Services</th>
          <th class="sel-h">Select where applicable</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, i) in SERVICES" :key="'srv-' + i">
          <td>
            {{ item.no }} {{ item.text }}
            <div v-for="s in item.sub || []" :key="s" class="sub">{{ s }}</div>
          </td>
          <td class="yn-cell sel-cell">
            <span class="cdd-cbx" @click="toggleSrv(i + 1)">
              <span class="box">{{ srvOn[`srv_a_${i + 1}`] ? '✓' : '' }}</span>
            </span>
          </td>
          <td>
            <CddVarField
              v-if="srvOn[`srv_a_${i + 1}`]"
              :field-key="`srv_a_${i + 1}_details`"
              :label="`Details`"
            />
            <span v-else class="muted">—</span>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Section B - Risk Assessment of Customer</p>
    <p class="doc-p">
      You need to understand the ML/PF/TF risks associated with your customers. The following checklists are examples of
      factors that you should consider when performing a risk assessment. The checklists are not exhaustive and should be
      augmented on a case-by-case basis.
    </p>

    <p class="doc-sec-title">B1 Customer’s Risk Factors</p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, i) in B1" :key="'b1-' + i">
          <td>
            {{ item.no }} {{ item.text }}<sup v-if="item.note">{{ item.note }}</sup>
            <div v-for="s in item.sub || []" :key="s" class="sub">{{ s }}</div>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b1Ans, `b1_q${i + 1}`, 'yes')">
              <span class="box">{{ b1Ans[`b1_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b1Ans, `b1_q${i + 1}`, 'no')">
              <span class="box">{{ b1Ans[`b1_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p note">
      If “Yes” has been selected for most of the 18 questions above, you are advised to adopt a risk-sensitive approach and
      perform enhanced CDD measures before establishing a business relationship with the customer.
    </p>

    <p class="doc-sec-title">B2 Country/Territory Risk Factors</p>
    <p class="doc-p note">
      Consider the following factors (if applicable): Customer’s nationality/dual nationalities, Place of
      formation/incorporation, Residential address, Permanent address, Place of operation, Place where business is
      established; etc.
    </p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, i) in B2" :key="'b2-' + i">
          <td>{{ item.no }} {{ item.text }}<sup v-if="item.note">{{ item.note }}</sup></td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b2Ans, `b2_q${i + 1}`, 'yes')">
              <span class="box">{{ b2Ans[`b2_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b2Ans, `b2_q${i + 1}`, 'no')">
              <span class="box">{{ b2Ans[`b2_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p note">If “Yes” has been selected for question 1, you must perform enhanced CDD measures.</p>
    <p class="doc-p note">
      If “Yes” has been selected for questions 2 to 4 above, you are advised to adopt a risk-sensitive approach and perform
      enhanced CDD measures before establishing a business relationship with the customer.
    </p>

    <p class="doc-sec-title">B3 Services/ Transactions Risk Factors</p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
          <th class="yn-h">NA</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, i) in B3" :key="'b3-' + i">
          <td>
            {{ item.no }} {{ item.text }}
            <div v-for="s in item.sub || []" :key="s" class="sub">{{ s }}</div>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'yes')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'no')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'na')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'na' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p note">
      If “Yes” has been selected for most of the 11 questions above, you are advised to adopt a risk-sensitive approach and
      perform enhanced CDD measures before establishing a business relationship with the customer/customer.
    </p>
    <p class="doc-p note">
      If “No” has been selected for question 10 and/or 11, you must take measures to compensate for the higher risk for such
      remote transactions.
    </p>

    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Customer Risk Rating</td>
          <td>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'low')">
              Low <span class="box">{{ riskRating.value === 'low' ? '✓' : '' }}</span>
            </span>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'medium')">
              Medium <span class="box">{{ riskRating.value === 'medium' ? '✓' : '' }}</span>
            </span>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'high')">
              High <span class="box">{{ riskRating.value === 'high' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Reasons for customer’s risks rating:</td>
          <td><CddVarField field-key="risk_rating_reasons" label="Risk Rating Reasons" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">
            Reliance on third party (Regulation 24(1))
            <div class="muted small">where applicable</div>
          </td>
          <td>
            <span class="cdd-cbx" @click="toggleReliance">
              <span class="box">{{ reliance.on ? '✓' : '' }}</span> Include in the form
            </span>
            <div v-if="reliance.on" class="reliance">
              <p>
                In accordance with Regulation 24(1) of the Corporate Service Providers Regulations 2025, where applicable, a
                Corporate Service Provider may rely on a third party to perform any customer due diligence.
              </p>
              <p>
                Aura Group has been appointed by <CddVarField field-key="entity_name" label="Name of Company" /> to act as the
                Investment Manager for itself.
              </p>
              <p>
                Aura Group has completed the customer due diligence process and that the customer due diligence checks,
                identification documents, and supporting documents of the directors, shareholders, and beneficial owners of
                <CddVarField field-key="entity_name" label="Name of Company" /> are valid, accurate, and up to date.
              </p>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">
      Recommendation For Acceptance of Customer<sup>31</sup>
      <span class="fw-normal">(Please attach documentation of the reasons for accepting a high-risk customer.)</span>
    </p>
    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Recommendation</td>
          <td>
            <span class="cdd-cbx" @click="setOne(recommendation, 'recommendation', 'recommended')">
              Recommended <span class="box">{{ recommendation.value === 'recommended' ? '✓' : '' }}</span>
            </span>
            <span class="cdd-cbx" @click="setOne(recommendation, 'recommendation', 'not_recommended')">
              Not Recommended<sup>32</sup> <span class="box">{{ recommendation.value === 'not_recommended' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Name of Recommending Officer:</td>
          <td><CddVarField field-key="recommending_officer_name" label="Recommending Officer Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Position:</td>
          <td><CddVarField field-key="recommending_officer_position" label="Position" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date:</td>
          <td><CddVarField field-key="recommending_officer_date" label="Date" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Signature:</td>
          <td class="risk-sig-cell">
            <SgPreSigners :var-key="SG_PRE_SIGNER_KEYS.riskRecommend" />
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">
      Approval for Acceptance of Customer
      <span class="fw-normal">(Please attach documentation of the reasons where approval is contrary to the recommendation.)</span>
    </p>
    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Approval</td>
          <td>
            <span class="cdd-cbx" @click="setOne(approval, 'approval', 'approved')">
              Approved <span class="box">{{ approval.value === 'approved' ? '✓' : '' }}</span>
            </span>
            <span class="cdd-cbx" @click="setOne(approval, 'approval', 'not_approved')">
              Not Approved<sup>26</sup> <span class="box">{{ approval.value === 'not_approved' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Name of Approving Officer:</td>
          <td><CddVarField field-key="approving_officer_name" label="Approving Officer Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Position:</td>
          <td><CddVarField field-key="approving_officer_position" label="Position" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date:</td>
          <td><CddVarField field-key="approving_officer_date" label="Date" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Signature:</td>
          <td class="risk-sig-cell">
            <SgPreSigners :var-key="SG_PRE_SIGNER_KEYS.riskApprove" />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="footnotes">
      <p v-for="[n, t] in FOOTNOTES" :key="n"><sup>{{ n }}</sup> {{ t }}</p>
      <p class="rfa">RFA Guidelines Issued on 9 May 2025</p>
    </div>
  </div>
</template>

<style scoped>
.risk-doc :deep(.cdd-cbx) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 14px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.risk-doc :deep(.cdd-cbx .box) {
  display: inline-flex;
  width: 13px;
  height: 13px;
  border: 1.5px solid #111;
  margin-right: 2px;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  background: #fff;
}
.yn-cell { text-align: center; vertical-align: middle; width: 52px; }
.yn-cell :deep(.cdd-cbx) { margin-right: 0; justify-content: center; }
.yn-cell.sel-cell { width: auto; }
.muted { color: #9aa6bd; }
.small { font-size: 11px; font-weight: 400; }
.note { font-size: 11.5px; color: #555; line-height: 1.45; margin: 8px 0 10px; font-style: italic; }
.sub { padding-left: 14px; margin-top: 3px; }
.red { color: #d0021b; }
.fw-normal { font-weight: 400; }
.risk-tbl th { font-size: 11px; background: #f3f5f9; }
.risk-tbl td { font-size: 12px; line-height: 1.45; vertical-align: top; }
.risk-tbl thead th.yn-h { text-align: center; width: 64px; }
.risk-tbl thead th.sel-h { text-align: center; white-space: normal; word-break: keep-all; }
.risk-sig-cell { padding-top: 6px; padding-bottom: 6px; }
.risk-sig-cell :deep(.sgf) { max-width: 420px; }
.risk-letterhead { border-bottom: 3px solid #1f3b73; padding-bottom: 6px; margin-bottom: 14px; }
.lh-name { font-size: 15px; font-weight: 600; }
.lh-meta { font-size: 10.5px; color: #333; }
.head-tbl { margin-bottom: 12px; }
.reliance { margin-top: 6px; font-style: italic; font-size: 12px; line-height: 1.5; }
.reliance p { margin: 0 0 6px; }
.footnotes { margin-top: 18px; border-top: 1px solid #ccc; padding-top: 6px; font-size: 10.5px; color: #444; }
.footnotes p { margin: 0 0 3px; }
.footnotes .rfa { text-align: right; margin-top: 8px; }
</style>
